import type { MaterializedMenuProduct } from '@iheartjane/data-access';
import { formatCurrency } from '@iheartjane/utils';
import intersection from 'lodash-es/intersection';

import type { CartItem } from '@jane/bloom/types/schemas/bloom/checkout';
import type { ProductApplicableIds } from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import { formatWeight } from '@jane/bloom/utils/strings/formatWeight';
import { getPercentage } from '@jane/bloom/viewModels/getPercentage';
import {
  getPriceBeforeDiscounts,
  getPriceIncludingDiscounts,
} from '@jane/bloom/viewModels/getPrice';
import {
  getBaseLabResults,
  getLabResults,
} from '@jane/bloom/viewModels/potencyInfo';

export const formatAvailableWeights = (
  product: MaterializedMenuProduct,
  weightsInCart: CartItem[],
  applicablePriceIds?: ProductApplicableIds[] | null
) => {
  const weights =
    applicablePriceIds != null && applicablePriceIds.length > 0
      ? [...intersection(applicablePriceIds, product.available_weights), 'each']
      : [...product.available_weights, 'each'];

  return weights.reduce((acc, curr) => {
    return {
      ...acc,
      [curr]: {
        basePotencyLabResults: getBaseLabResults({
          percentCBDA: getPercentage(product.percent_cbda),
          percentTHCA: getPercentage(product.percent_thca),
          primaryPercentCBD: getPercentage(product.percent_cbd),
          primaryPercentTHC: getPercentage(product.percent_thc),
          productPercentTAC: getPercentage(product.percent_tac),
        }),
        formattedWeight: formatWeight(curr),
        fullPriceFormatted: formatCurrency(
          getPriceBeforeDiscounts(product, curr),
          true
        ),
        isInCart: !!weightsInCart?.find(
          (weightInCart: CartItem) =>
            weightInCart.priceId === formatWeight(curr)
        ),
        potencyLabResults: getLabResults({
          percentCBDA: getPercentage(product.percent_cbda),
          percentTHCA: getPercentage(product.percent_thca),
          primaryPercentCBD: getPercentage(product.percent_cbd),
          primaryPercentTHC: getPercentage(product.percent_thc),
          product,
          productPercentTAC: getPercentage(product.percent_tac),
          selectedWeight: formatWeight(curr),
        }),
        priceFormatted: formatCurrency(
          getPriceIncludingDiscounts(product, curr),
          true
        ),
      },
    };
  }, {});
};
